import {
  ListItem,
  NotificationFormModel,
  NotificationSettingInput,
} from './typings'
import {
  SalesTeamHierarchy,
  NotificationSalesTeamPosition,
} from '@user/store/notifications/typings'

const idToNumber = (id: string): number => Number(id.split('_')[1])
const idToString = (id: string): string => id.split('_')[1]

export const getChildren = (parent: ListItem, list: ListItem[]): ListItem[] =>
  parent.id.startsWith('user')
    ? []
    : list.reduce<ListItem[]>(
        (prev, curr) =>
          curr.parentId === parent.id
            ? [...prev, curr, ...getChildren(curr, list)]
            : prev,
        [],
      )

export const getParents = (item: ListItem, list: ListItem[]): ListItem[] =>
  item.id.startsWith('team')
    ? []
    : list.reduce<ListItem[]>(
        (prev, curr) =>
          item.parentId === curr.id
            ? [...prev, curr, ...getParents(curr, list)]
            : prev,
        [],
      )

export const toNotificationSettingInput = ({
  list,
  additional,
}: NotificationFormModel): NotificationSettingInput => {
  type Node = ListItem & { children: Node[] }

  const map = new Map<string, Node>(
    list.map<[string, Node]>((x) => [
      // users can be on team in multiple places, ids must be unique
      x.id.startsWith('user') ? `${x.id}_${x.parentId}` : x.id,
      { ...x, children: [] },
    ]),
  )

  const tree: Node[] = []

  map.forEach((node) => {
    if (node.selected || node.indeterminate) {
      if (node.parentId === null) {
        tree.push(node)
      } else {
        const parent = map.get(node.parentId)
        // prune tree at selected nodes
        if (!!parent && parent.indeterminate) {
          parent.children.push(node)
        }
      }
    }
  })

  const salesTeamHierarchy = tree.reduce<SalesTeamHierarchy>(
    (prev, { id, selected, children }) => {
      const { salesTeamIds, salesTeamPositions } = prev
      if (selected) {
        salesTeamIds.push(idToNumber(id))
      } else {
        salesTeamPositions.push(
          ...children.map<NotificationSalesTeamPosition>(
            ({ id, selected, children }) => ({
              id: idToNumber(id),
              isSelected: selected,
              employees: children.map(({ id }) => idToString(id)),
            }),
          ),
        )
      }
      return prev
    },
    { salesTeamIds: [], salesTeamPositions: [] },
  )

  return {
    additionalEmailIds: additional,
    salesTeamHierarchy,
  }
}
